import { LocaleService } from "../services/localeService";
import { DomUtils } from "../utils/domUtils";

export const PasswordComplexityConstants = {
	passwordWarningClass: "password-warning",
	passwordComplexityClass: "password-complexity",
	ruleListClass: "password-complexity-list",
	ruleClass: "password-complexity-rule",
	ruleFailedClass: "failed",
	rulePassedClass: "passed"
};

export class PasswordComplexity {
	static Init(passwordInput, pattern) {
		return new PasswordComplexity(passwordInput, pattern);
	}

	constructor(passwordInput, domainPasswordComplexity) {
		this._localeService = new LocaleService();

		this._input = DomUtils.GetElement(passwordInput);

		const { passwordComplexityRules, passwordComplexityPattern } = this._IdentifyPasswordComplexityType(domainPasswordComplexity);

		this._passwordComplexityRules = passwordComplexityRules ?? null;
		this._passwordComplexityPattern = !!passwordComplexityPattern ? new RegExp(passwordComplexityPattern) : null;

		if (passwordComplexityRules != null) {
			this._InitPasswordComplexityRules(passwordComplexityRules);
		}
		else {
			this._InitPasswordComplexityPattern(passwordComplexityPattern);
		}
	}

	_IdentifyPasswordComplexityType(domainPasswordComplexity) {
		domainPasswordComplexity = domainPasswordComplexity || "";
		const complexityConfigCheck = /^\s*\{[^}]*\}\s*$/g;

		let passwordComplexityRules = null;
		let passwordComplexityPattern = null;

		if (complexityConfigCheck.test(domainPasswordComplexity)) {
			try {
				const passwordComplexityRuleObject = JSON.parse(domainPasswordComplexity);
				passwordComplexityRules = Object.keys(passwordComplexityRuleObject).map(function (key) {
					return { name: key, value: passwordComplexityRuleObject[key] };
				});
			}
			catch (e) {
				console.error("Error parsing password complexity pattern: " + e);
			}
		}
		else {
			passwordComplexityPattern = domainPasswordComplexity;
		}

		return { passwordComplexityRules, passwordComplexityPattern };
	}

	_InitPasswordComplexityPattern() {
		const patternWarning = document.createElement("label");
		patternWarning.classList.add(PasswordComplexityConstants.passwordWarningClass);
		DomUtils.ShowHide(false, patternWarning);

		const patternWarningSpan = document.createElement("span");
		patternWarning.appendChild(patternWarningSpan);
		this._localeService.SetElementTranslation(patternWarningSpan, "oauth.passwordComplexity");

		this._patternWarning = patternWarning;
		this._input.closest("label")?.insertAdjacentElement("afterend", patternWarning);

		this._BindPasswordPatternEvents();
		this._SetupPasswordPatternValidation();
	}

	_BindPasswordPatternEvents() {
		this._input.addEventListener("keyup", () => {
			this._ValidatePasswordComplexityPattern();
		});
	}

	_SetupPasswordPatternValidation() {
		this._input.validate = () => this._ValidatePasswordComplexityPattern();
		this._ValidatePasswordComplexityPattern();
	}

	_ValidatePasswordComplexityPattern() {
		const value = this._input.value;

		let result;

		if (!!value && !this._passwordComplexityPattern.test(value)) {
			DomUtils.ShowHide(true, this._patternWarning);
			result = false;
		}
		else {
			DomUtils.ShowHide(false, this._patternWarning);
			result = true;
		}

		this._SetPasswordInputValidationState(result);

		return result;
	}

	_InitPasswordComplexityRules(passwordComplexityRules) {
		const complexityWarning = document.createElement("div");
		complexityWarning.classList.add(PasswordComplexityConstants.passwordComplexityClass);
		DomUtils.ShowHide(true, complexityWarning);

		const passwordComplexityList = document.createElement("ul");
		passwordComplexityList.classList.add(PasswordComplexityConstants.ruleListClass);
		complexityWarning.appendChild(passwordComplexityList);

		for (let i = 0; i < passwordComplexityRules.length; i++) {
			const rule = passwordComplexityRules[i];

			const passwordComplexityListItem = document.createElement("li");
			passwordComplexityListItem.classList.add(`${PasswordComplexityConstants.ruleClass}-${rule.name}`);
			passwordComplexityListItem.classList.add(PasswordComplexityConstants.ruleFailedClass);
			passwordComplexityList.appendChild(passwordComplexityListItem);

			const passwordComplexityListItemSpan = document.createElement("span");
			passwordComplexityListItem.appendChild(passwordComplexityListItemSpan);

			this._localeService.SetElementTranslation(
				passwordComplexityListItemSpan,
				`oauth.passwordComplexity.${rule.name}|${rule.value}`
			);
		}

		this._complexityWarning = complexityWarning;
		this._input.closest("label")?.insertAdjacentElement("afterend", complexityWarning);

		this._BindPasswordComplexityRuleEvents();
		this._SetupPasswordComplexityRuleValidation();
	}

	_BindPasswordComplexityRuleEvents() {
		this._input.addEventListener("focus", () => {
			this._TogglePasswordComplexityRules(true);
		});

		this._input.addEventListener("blur", () => {
			this._TogglePasswordComplexityRules(false);
		});

		this._input.addEventListener("keyup", () => {
			this._ValidatePasswordComplexityRules();
		});
	}

	_SetupPasswordComplexityRuleValidation() {
		this._input.validate = () => this._ValidatePasswordComplexityRules();
		this._ValidatePasswordComplexityRules();
	}

	_TogglePasswordComplexityRules(visible) {
		DomUtils.ShowHide(visible, this._complexityWarning);
	}

	_ValidatePasswordComplexityRules() {
		const rules = this._passwordComplexityRules;
		const password = this._input.value;

		if (rules == null || !password) {
			this._SetPasswordInputValidationState(false);
			return false;
		}

		const passwordComplexity = {};
		for (var i = 0; i < rules.length; i++) {
			var rule = rules[i];

			if (!rule.value) {
				continue;
			}

			var pattern = null;
			switch (rule.name) {
				case "length":
					pattern = /./g;
					break;
				case "upper":
					pattern = /[A-Z]/g;
					break;
				case "lower":
					pattern = /[a-z]/g;
					break;
				case "digit":
					pattern = /[0-9]/g;
					break;
				case "special":
					pattern = /[^a-zA-Z0-9]/g;
					break;
			}

			if (!!pattern) {
				const rulePassed = (password.match(pattern) || []).length >= rule.value;

				const ruleElement = this._complexityWarning.querySelector(`.${PasswordComplexityConstants.ruleClass}-${rule.name}`);
				ruleElement.classList.toggle(PasswordComplexityConstants.ruleFailedClass, !rulePassed);
				ruleElement.classList.toggle(PasswordComplexityConstants.rulePassedClass, !!rulePassed);

				passwordComplexity[rule.name] = rulePassed;
			}
		}

		const result =
			(passwordComplexity.length ?? true) &&
			(passwordComplexity.upper ?? true) &&
			(passwordComplexity.lower ?? true) &&
			(passwordComplexity.digit ?? true) &&
			(passwordComplexity.special ?? true);

		this._SetPasswordInputValidationState(result);

		return result;
	}

	_SetPasswordInputValidationState(isValid) {
		this._input.classList.toggle("invalid", !isValid);
		if (isValid === false) {
			DomUtils.FocusElement(this._input);
		}
	}
}
